import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  message,
  Divider,
  Space,
  Tooltip,
  Input as AntInput,
  Tag,
  Spin,
  Select
} from 'antd';
import { SendOutlined, SearchOutlined, RedoOutlined, TeamOutlined, UserAddOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Mock data for testing purposes
const ACCESS_LEVELS = [
  { label: 'All', value: 'all' },
  { label: 'Medical Note', value: 'medical_note' },
  { label: 'Patient Visit Summary', value: 'patient_visit_summary' },
  { label: 'Health Assessment Report', value: 'health_assessment_report' },
  { label: 'Audio File', value: 'audio_file' },
  { label: 'Transcript', value: 'transcript' }
];

const MOCK_TEAMS = [
  {
    id: '1',
    name: 'Demo Health Team',
    accessLevels: ['medical_note', 'patient_visit_summary'],
    createdAt: new Date().toISOString()
  }
];

const MOCK_MEMBERS = [
  {
    id: '1',
    email: 'admin@example.com',
    status: 'Accepted',
    invitationDate: '2025-03-10T10:00:00Z'
  },
  {
    id: '2',
    email: 'doctor@example.com',
    status: 'Accepted',
    invitationDate: '2025-03-11T14:30:00Z'
  },
  {
    id: '3',
    email: 'nurse@example.com',
    status: 'Pending',
    invitationDate: '2025-03-12T09:15:00Z'
  },
  {
    id: '4',
    email: 'technician@example.com',
    status: 'Pending',
    invitationDate: '2025-03-13T16:45:00Z'
  }
];

// Helper function to simulate API delay
const simulateApiDelay = (ms = 800) => new Promise(resolve => setTimeout(resolve, ms));

const TeamAccess = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allTeams, setAllTeams] = useState(MOCK_TEAMS);
  const [hasExistingTeam, setHasExistingTeam] = useState(false);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [showManageTeam, setShowManageTeam] = useState(false);
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [addUserForm] = Form.useForm();

  // Define checkUserTeams with useCallback to avoid recreation on each render
  const checkUserTeams = useCallback(async () => {
    setLoading(true);
    try {
      // Simulate API call with mock data
      await simulateApiDelay();
      
      // Use mock data instead of actual API call
      setAllTeams(MOCK_TEAMS);
      
      // Check if user has at least one team
      if (MOCK_TEAMS.length > 0) {
        setHasExistingTeam(true);
        
        // Set current team to the first team (could be enhanced to remember last selected team)
        const team = MOCK_TEAMS[0];
        setCurrentTeam(team);
      } else {
        setHasExistingTeam(false);
        setCurrentTeam(null);
      }
    } catch (error) {
      console.error('Error fetching user teams:', error);
      message.error('An error occurred while fetching user teams.');
    }
    setLoading(false);
  }, []);  // No external dependencies for the mock version

  useEffect(() => {
    checkUserTeams();
  }, [checkUserTeams]);

  useEffect(() => {
    if (currentTeam) {
      fetchTeamMembers();
    }
  }, [pagination.current, searchText, currentTeam]);

  const fetchTeamMembers = async () => {
    if (!currentTeam) return;
    
    setLoading(true);
    try {
      // Simulate API delay
      await simulateApiDelay();
      
      // Filter mock members based on search text if any
      let filteredMembers = [...MOCK_MEMBERS];
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        filteredMembers = filteredMembers.filter(member => 
          member.email.toLowerCase().includes(searchLower)
        );
      }
      
      // Apply pagination
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedMembers = filteredMembers.slice(startIndex, endIndex);
      
      setTeamMembers(paginatedMembers);
      setPagination({
        ...pagination,
        total: filteredMembers.length
      });
    } catch (error) {
      console.error('Error fetching team members:', error);
      message.error('An error occurred while fetching team members.');
    }
    setLoading(false);
  };

  const handleCreateTeam = values => {
    Modal.confirm({
      title: 'Confirm Team Creation',
      content: `Are you sure you want to create a team named "${values.teamName}" with the selected access levels?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        setInviteLoading(true);
        try {
          const emails = values.emails
            .split(/[,;]/)
            .map(email => email.trim())
            .filter(email => email);

          // Simulate API delay
          await simulateApiDelay();
          
          // Create mock team
          const newTeam = {
            id: String(Date.now()),
            name: values.teamName,
            accessLevels: values.accessLevels,
            inheritFrom: values.inheritFrom || [],
            createdAt: new Date().toISOString()
          };
          
          // Create mock members from emails
          const newMembers = emails.map((email, index) => ({
            id: String(Date.now() + index + 1),
            email,
            status: 'Pending',
            invitationDate: new Date().toISOString()
          }));
          
          // Update mock data
          MOCK_TEAMS.push(newTeam);
          MOCK_MEMBERS.push(...newMembers);
          
          // Update state to include new team
          setAllTeams([...allTeams, newTeam]);
          
          message.success('Team created and invitations sent successfully!');
          form.resetFields();
          
          // Update state to show the newly created team
          setCurrentTeam(newTeam);
          setHasExistingTeam(true);
          
          // Fetch user teams again to update the list
          await checkUserTeams();
        } catch (error) {
          console.error('Error creating team:', error);
          message.error('An error occurred while creating the team.');
        }
        setInviteLoading(false);
      }
    });
  };

  const handleReinvite = (record) => {
    Modal.confirm({
      title: 'Confirm Reinvitation',
      content: `Are you sure you want to resend invitation to ${record.email}?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          // Simulate API delay
          await simulateApiDelay();
          
          message.success(`Invitation resent to ${record.email}`);
          fetchTeamMembers();
        } catch (error) {
          console.error('Error resending invitation:', error);
          message.error('An error occurred while resending invitation.');
        }
      }
    });
  };

  const handleAddMember = () => {
    addUserForm.validateFields().then(values => {
      Modal.confirm({
        title: 'Confirm Invitation',
        content: `Are you sure you want to invite ${values.email} to the team?`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          try {
            // Simulate API delay
            await simulateApiDelay();
            
            // Create new mock member
            const newMember = {
              id: String(Date.now()),
              email: values.email,
              status: 'Pending',
              invitationDate: new Date().toISOString()
            };
            
            // Add to mock data
            MOCK_MEMBERS.push(newMember);
            
            message.success(`Invitation sent to ${values.email}`);
            addUserForm.resetFields();
            fetchTeamMembers();
          } catch (error) {
            console.error('Error sending invitation:', error);
            message.error('An error occurred while sending invitation.');
          }
        }
      });
    });
  };

  const handleRemoveMember = (record) => {
    Modal.confirm({
      title: 'Confirm Removal',
      content: `Are you sure you want to remove ${record.email} from the team?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          // Simulate API delay
          await simulateApiDelay();
          
          // Remove from mock data
          const index = MOCK_MEMBERS.findIndex(member => member.id === record.id);
          if (index !== -1) {
            MOCK_MEMBERS.splice(index, 1);
          }
          
          message.success(`${record.email} removed from the team`);
          fetchTeamMembers();
        } catch (error) {
          console.error('Error removing team member:', error);
          message.error('An error occurred while removing the team member.');
        }
      }
    });
  };

  const onSearch = value => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span style={{ color: status === 'Accepted' ? 'green' : 'orange' }}>
          {status}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status)
    },
    {
      title: 'Invitation Date',
      dataIndex: 'invitationDate',
      key: 'invitationDate',
      sorter: (a, b) => new Date(a.invitationDate) - new Date(b.invitationDate)
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {record.status !== 'Accepted' && (
            <Tooltip title="Resend Invitation">
              <Button 
                icon={<RedoOutlined />} 
                onClick={() => handleReinvite(record)}
                type="text"
              />
            </Tooltip>
          )}
          <Tooltip title="Remove Member">
            <Button 
              icon={<DeleteOutlined />} 
              onClick={() => handleRemoveMember(record)}
              type="text"
              danger
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const renderExistingTeamView = () => (
    <div style={{ padding: '0 20px' }}>
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <TeamOutlined style={{ marginRight: 8 }} />
              {currentTeam?.name || 'Your Team'}
            </span>
            <Space>
              <Button 
                type="primary"
                onClick={() => setShowManageTeam(!showManageTeam)}
              >
                {showManageTeam ? 'Hide Management' : 'Manage Team'}
              </Button>
              <Tooltip title="Create New Team">
                <Button
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setShowCreateTeamForm(true)}
                  shape="circle"
                />
              </Tooltip>
            </Space>
          </div>
        }
        style={{ marginBottom: 20 }}
      >
        <div>
          <Text style={{ marginRight: 16 }}>
            <strong>Access Levels:</strong> 
          </Text>
          {currentTeam?.accessLevels?.map(level => {
            const accessLevel = ACCESS_LEVELS.find(l => l.value === level);
            return (
              <Tag color="blue" key={level} style={{ margin: '4px' }}>
                {accessLevel ? accessLevel.label : level}
              </Tag>
            );
          })}
        </div>
        
        {showManageTeam && (
          <div style={{ marginTop: 20 }}>
            <Divider>Add New Team Member</Divider>
            <Form
              form={addUserForm}
              layout="inline"
              style={{ marginBottom: 20 }}
            >
              <Form.Item
                name="email"
                rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
                style={{ width: '60%' }}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={handleAddMember}
                >
                  Invite
                </Button>
              </Form.Item>
            </Form>
            
            <Divider>Team Members</Divider>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <AntInput
                placeholder="Search by email"
                prefix={<SearchOutlined />}
                style={{ width: 300 }}
                allowClear
                onPressEnter={(e) => onSearch(e.target.value)}
                onChange={(e) => {
                  if (!e.target.value) {
                    onSearch('');
                  }
                }}
              />
            </div>
            <Table
              columns={columns}
              dataSource={teamMembers}
              rowKey="id"
              pagination={pagination}
              loading={loading}
              onChange={handleTableChange}
            />
          </div>
        )}
      </Card>

      {/* Create Team Modal */}
      <Modal
        title="Create New Team"
        open={showCreateTeamForm}
        onCancel={() => setShowCreateTeamForm(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            handleCreateTeam(values);
            setShowCreateTeamForm(false);
          }}
        >
          <Form.Item
            name="teamName"
            label="Team Name"
            rules={[{ required: true, message: 'Please enter a team name' }]}
          >
            <Input placeholder="Enter team name" />
          </Form.Item>
          
          <Form.Item
            name="accessLevels"
            label="Access Levels"
            rules={[{ required: true, message: 'Please select at least one access level' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select access levels"
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {ACCESS_LEVELS.map(level => (
                <Option key={level.value} value={level.value} label={level.label}>
                  {level.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {allTeams.length > 0 && (
            <Form.Item
              name="inheritFrom"
              label="Inherit Access from Existing Teams"
            >
              <Select
                mode="multiple"
                placeholder="Select teams to inherit access from"
                style={{ width: '100%' }}
                optionLabelProp="label"
              >
                {allTeams.map(team => (
                  <Option key={team.id} value={team.id} label={team.name}>
                    {team.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          <Form.Item
            name="emails"
            label="Invite Users (Email addresses separated by comma or semicolon)"
            rules={[{ required: true, message: 'Please enter at least one email address' }]}
          >
            <TextArea
              placeholder="Enter email addresses separated by comma or semicolon"
              rows={4}
            />
          </Form.Item>
          
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={inviteLoading}
            >
              Create Team & Send Invitations
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const renderCreateTeamView = () => (
    <div style={{ padding: '0 20px' }}>
      <Card title="Create Team & Invite Users" style={{ marginBottom: 20 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTeam}
        >
          <Form.Item
            name="teamName"
            label="Team Name"
            rules={[{ required: true, message: 'Please enter a team name' }]}
          >
            <Input placeholder="Enter team name" />
          </Form.Item>
          
          <Form.Item
            name="accessLevels"
            label="Access Levels"
            rules={[{ required: true, message: 'Please select at least one access level' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select access levels"
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {ACCESS_LEVELS.map(level => (
                <Option key={level.value} value={level.value} label={level.label}>
                  {level.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="emails"
            label="Invite Users (Email addresses separated by comma or semicolon)"
            rules={[{ required: true, message: 'Please enter at least one email address' }]}
          >
            <TextArea
              placeholder="Enter email addresses separated by comma or semicolon"
              rows={4}
            />
          </Form.Item>
          
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={inviteLoading}
            >
              Create Team & Send Invitations
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );

  return (
    <div>
      {loading && !hasExistingTeam ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      ) : (
        hasExistingTeam ? renderExistingTeamView() : renderCreateTeamView()
      )}
    </div>
  );
};

export default TeamAccess;
